import { AddressDto, ContactDto, CreateAddressDto, GetSpecialConditionsResponse } from 'api/borrower';
import {
  AddressFormValues,
  ContactFormValues,
  SpecialConditionsFormValues,
  specialConditionsFormConfig
} from './specialConditionForm';
import { InsuranceProjectType } from './BorrowerSpecialConditionsLayout';
import { toDayInput } from 'core/utils/dates';

// ============================================================================
// Address Mappers
// ============================================================================

export const mapAddressDtoToFormValues = (
  address?: AddressDto | CreateAddressDto
): AddressFormValues | undefined => {
  if (!address) return undefined;
  return {
    address: address.line1,
    additionalAddress: address.line2 || (address as AddressDto).line3 || undefined,
    postalCode: address.zipCode,
    city: address.city,
    country: address.country,
    googlePlaceId: address.googlePlaceId ?? undefined,
    latitude: address.latitude ?? undefined,
    longitude: address.longitude ?? undefined
  };
};

export const mapFormValuesToAddressDto = (
  address?: AddressFormValues
) => {
  if (!address || !address.city) return undefined;
  return {
    line1: address.address || '',
    line2: address.additionalAddress?.trim() || '',
    zipCode: address.postalCode || '',
    city: address.city,
    country: address.country || '',
    googlePlaceId: address.googlePlaceId,
    latitude: address.latitude,
    longitude: address.longitude
  };
};

const formatAddress = (address?: AddressDto | CreateAddressDto): string | undefined => {
  if (!address) return undefined;
  const formatted = (address as AddressDto).formattedAddress;
  if (formatted) return formatted;
  const parts = [address.line1, address.zipCode, address.city].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : undefined;
};

// ============================================================================
// Contact Mapper
// ============================================================================

const emptyContact: ContactFormValues = {
  entityName: '', entityAddressSearch: '',
  lastName: '', firstName: '', phone: '', email: ''
};

const mapContactDtoToFormValues = (contact?: ContactDto): ContactFormValues => {
  if (!contact) return emptyContact;
  return {
    entityName: contact.entityName,
    entityAddressSearch: formatAddress(contact.entityAddress),
    entityAddress: mapAddressDtoToFormValues(contact.entityAddress),
    lastName: contact.lastName,
    firstName: contact.firstName,
    phone: contact.phone,
    email: contact.email
  };
};

// ============================================================================
// Response to Form Values Mapper
// ============================================================================

export const mapResponseToFormValues = (
  response: GetSpecialConditionsResponse | null,
  frequencyCode: string
): SpecialConditionsFormValues => {
  if (!response) {
    return specialConditionsFormConfig.initialValues;
  }

  return {
    bankAccountId: response.bankAccountId || '',
    frequencyCode: frequencyCode,
    // Date calendaire : découpe de string, jamais Date -> toISOString (décale d'un jour)
    startDate: toDayInput(response.startDate),
    existingOutstanding: response.existingOutstanding ?? 0,
    existingOutstandingInsurer: response.existingOutstandingInsurer ?? 0,
    currentLoanNumber: response.currentLoanNumber ?? '',
    bankContact: mapContactDtoToFormValues(response.bankContact),
    insuranceContact: mapContactDtoToFormValues(response.insuranceContact)
  };
};

// ============================================================================
// Insurance Project Type Helper
// ============================================================================

export const getInsuranceProjectType = (
  response: GetSpecialConditionsResponse | null
): InsuranceProjectType => {
  if (!response) return null;
  if (response.insuranceProjectCode === 'RENEG_BANQUE') return 'RENEG_BANQUE';
  if (response.insuranceProjectCode === 'RENEG_COURTIER') return 'RENEG_COURTIER';
  return null;
};
